import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';

/**
 * Reusable destructive-action confirm modal (#97).
 *
 * Use when an action is hard to reverse: marcar perdida, retirar herramienta,
 * forzar devolución, etc. The button stays disabled until the user types
 * the confirmText (typically a serial) and, when reasonRequired, also fills
 * the reason field.
 *
 * Props:
 *   show, onHide              — visibility control
 *   title                     — modal title
 *   icon                      — Bootstrap Icons class shown next to the title
 *   description               — short intro paragraph (string or node)
 *   consequences              — array of strings rendered as bullet list
 *   confirmText               — string the user must type verbatim to enable submit
 *   confirmLabel              — what to show next to the input (e.g. "serial", "número")
 *   reasonRequired            — when true, a textarea must be filled
 *   reasonLabel               — label for the reason field
 *   reasonPlaceholder         — placeholder for the reason field
 *   submitLabel               — text on the confirm button
 *   variant                   — Bootstrap variant for the submit button (default 'danger')
 *   onConfirm({ reason })     — called with the typed reason when user confirms
 *   pending                   — when true, shows spinner and disables inputs
 *   error                     — error string to render in an Alert
 */
const DestructiveConfirmModal = ({
  show,
  onHide,
  title = 'Confirmar acción destructiva',
  icon = 'bi-exclamation-triangle',
  description,
  consequences = [],
  confirmText,
  confirmLabel = 'serial',
  reasonRequired = false,
  reasonLabel = 'Motivo',
  reasonPlaceholder = '',
  submitLabel = 'Confirmar',
  variant = 'danger',
  onConfirm,
  pending = false,
  error = null
}) => {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (show) { setTyped(''); setReason(''); }
  }, [show]);

  const matchOk = !confirmText || typed.trim() === String(confirmText).trim();
  const reasonOk = !reasonRequired || reason.trim().length > 0;
  const canConfirm = matchOk && reasonOk && !pending;

  const handle = () => {
    if (!canConfirm) return;
    onConfirm({ reason: reason.trim() });
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton={!pending}>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className={`bi ${icon} text-${variant}`} />
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {description && (
          <div className="mb-3" style={{ whiteSpace: 'pre-line' }}>
            {description}
          </div>
        )}

        {consequences.length > 0 && (
          <Alert variant={variant === 'danger' ? 'warning' : 'light'} className="mb-3">
            <strong className="d-block mb-2">Esta acción:</strong>
            <ul className="mb-0 ps-3">
              {consequences.map((c, i) => (<li key={i}>{c}</li>))}
            </ul>
          </Alert>
        )}

        {confirmText && (
          <Form.Group className="mb-3">
            <Form.Label>
              Escribí el <strong>{confirmLabel}</strong> para confirmar:{' '}
              <code className="ms-1">{confirmText}</code>
            </Form.Label>
            <Form.Control
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={String(confirmText)}
              autoComplete="off"
              disabled={pending}
              isInvalid={typed.length > 0 && !matchOk}
              isValid={matchOk && typed.length > 0}
            />
          </Form.Group>
        )}

        {reasonRequired && (
          <Form.Group>
            <Form.Label>
              {reasonLabel} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              disabled={pending}
            />
            <Form.Text className="text-muted">
              Queda registrado en el historial de la herramienta.
            </Form.Text>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={pending}>
          Cancelar
        </Button>
        <Button variant={variant} onClick={handle} disabled={!canConfirm}>
          {pending && <Spinner size="sm" animation="border" className="me-1" />}
          {submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DestructiveConfirmModal;
